import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
  Alert,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const websiteHref = (website) => {
  if (!website) return null;
  const raw = String(website).trim();
  if (!raw) return null;
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

const BUSINESS_TYPE_KEYS = {
  hotel: 'hotel',
  restaurant: 'restaurant',
  tour_operator: 'tourOperator',
  transport: 'transport',
  activity_provider: 'activityProvider',
  travel_agency: 'travelAgency',
  other: 'other',
};

/**
 * Resolve agent fields without falling back to inquiry/booking traveler columns
 * (e.g. inquiry.email is the customer, not the partner).
 */
export const resolveAgentDetails = (agent = {}) => {
  const fromPartnerPrefix = Boolean(
    agent.partner_name
    || agent.partner_contact_name
    || agent.partner_website
    || agent.partner_address
  );

  if (fromPartnerPrefix) {
    return {
      name: agent.partner_name || '',
      contactName: agent.partner_contact_name || '',
      website: agent.partner_website || '',
      logo: agent.partner_logo || '',
      address: agent.partner_address || '',
      businessType: agent.partner_business_type || '',
      description: agent.partner_description || '',
    };
  }

  return {
    name: agent.company_name || agent.name || '',
    contactName: agent.contact_name || '',
    website: agent.website || '',
    logo: agent.logo || '',
    address: agent.company_address || agent.address || '',
    businessType: agent.business_type || '',
    description: agent.description || '',
  };
};

const DetailRow = ({ icon, label, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    <Box sx={{ color: 'action.active', mt: 0.35, display: 'flex' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      {children}
    </Box>
  </Box>
);

/**
 * Popup with travel-agent / partner contact details.
 * Accepts either flat inquiry/booking partner_* fields or a partner-shaped object.
 */
const AgentDetailsDialog = ({ open, onClose, agent }) => {
  const { t } = useTranslation();
  if (!agent) return null;

  const {
    name,
    contactName,
    website,
    logo,
    address,
    businessType,
    description,
  } = resolveAgentDetails(agent);
  const href = websiteHref(website);
  const businessTypeKey = BUSINESS_TYPE_KEYS[businessType];
  const businessTypeLabel = businessTypeKey
    ? t(`myInquiries.businessTypes.${businessTypeKey}`)
    : (businessType || '');

  const hasContact = Boolean(contactName || address || href || businessTypeLabel || description);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('myInquiries.agentDetailsTitle')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2.5 }}>
          {logo ? (
            <Avatar src={logo} alt={name} sx={{ width: 56, height: 56 }} />
          ) : (
            <Avatar sx={{ width: 56, height: 56, bgcolor: '#8B1538' }}>
              {(name || 'A').charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
              {name || t('myInquiries.agentFallback')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('myInquiries.verifiedPartner')}
            </Typography>
            {businessTypeLabel ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                {businessTypeLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, whiteSpace: 'pre-wrap' }}>
            {description}
          </Typography>
        ) : null}

        <Alert severity="info" sx={{ mb: 2.5 }}>
          {t('myInquiries.contactThroughInquiry')}
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {contactName ? (
            <DetailRow icon={<PersonIcon fontSize="small" />} label={t('myInquiries.contactPerson')}>
              <Typography variant="body1">{contactName}</Typography>
            </DetailRow>
          ) : null}

          {address ? (
            <DetailRow icon={<LocationIcon fontSize="small" />} label={t('myInquiries.address')}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{address}</Typography>
            </DetailRow>
          ) : null}

          {href ? (
            <DetailRow icon={<LanguageIcon fontSize="small" />} label={t('myInquiries.website')}>
              <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
                {website}
              </Link>
            </DetailRow>
          ) : null}

          {!hasContact ? (
            <DetailRow icon={<BusinessIcon fontSize="small" />} label={t('myInquiries.contactPerson')}>
              <Typography variant="body2" color="text.secondary">
                {t('myInquiries.noContactDetails')}
              </Typography>
            </DetailRow>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('myInquiries.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentDetailsDialog;
